import { Injectable, inject } from '@angular/core';
import { SideNavGroup, SideNavUser } from '@umss/shared/utils';
import {
  AuthService,
  AuthStorageService,
  Permission
} from '@umss/shared/data-access';
import { map, Observable } from 'rxjs';

@Injectable({
  providedIn: 'root'
})
export class NavigationService {
  private auth = inject(AuthService);
  private authStorage = inject(AuthStorageService);

  public readonly pathMap: Record<
    string,
    { path: string; permissions?: Permission[] }
  > = {
    home: { path: '/dashboard' },
    notifications: { path: '/notifications' },
    shifts: { path: '/scheduling/shifts', permissions: ['view_schedules'] },
    contractors: {
      path: '/contractors/contractors',
      permissions: ['view_contractors']
    },
    timesheets: {
      path: '/payments/timesheets',
      permissions: ['view_timesheets']
    },
    invoices: { path: '/payments/invoices', permissions: ['view_invoices'] },
    admins: { path: '/admin', permissions: ['view_admins'] },
    roles: { path: '/admin/roles', permissions: ['manage_roles'] },
    rates: { path: '/billable-rates', permissions: ['view_billing_rates'] },
    attendance: {
      path: '/settings/attendance',
      permissions: ['view_facility_settings']
    },
    billing: {
      path: '/settings/billing',
      permissions: ['manage_financial_integrations']
    },
    profile: {
      path: '/settings/facility-profile',
      permissions: ['view_facility_settings']
    },
    signOut: { path: '/auth/login' }
  };

  /**
   * Get facility navigation structure
   * Returns the side-nav groups configuration for the facility app
   * Matches Figma design structure exactly
   */
  getFacilityNavigation(): SideNavGroup[] {
    return [
      {
        label: '',
        items: [
          {
            label: 'Home',
            value: 'home',
            iconName: 'phosphorHouse',
            selected: false
          },
          {
            label: 'Notifications',
            value: 'notifications',
            iconName: 'phosphorBell',
            badge: 0,
            showBadge: false,
            selected: false
          }
        ]
      },
      {
        label: 'Scheduling',
        items: [
          {
            label: 'Shifts',
            value: 'shifts',
            iconName: 'phosphorCalendarBlank',
            hidden: this.isPathHidden('shifts'),
            selected: false
          },
          {
            label: 'Contractors',
            value: 'contractors',
            hidden: this.isPathHidden('contractors'),
            iconName: 'phosphorUsers',
            selected: false
          }
        ]
      },
      {
        label: 'Payments',
        items: [
          {
            label: 'Timesheets',
            value: 'timesheets',
            iconName: 'phosphorClock',
            selected: false,
            hidden: this.isPathHidden('timesheets')
          },
          {
            label: 'Invoices',
            value: 'invoices',
            iconName: 'phosphorReceipt',
            hidden: this.isPathHidden('invoices'),
            selected: false
          }
        ]
      },
      {
        label: 'Admins',
        items: [
          {
            label: 'Administrators',
            value: 'admins',
            iconName: 'phosphorUserPlus',
            badge: 0,
            showBadge: false,
            hidden: this.isPathHidden('admins'),
            selected: false
          },
          {
            label: 'Roles',
            value: 'roles',
            hidden: this.isPathHidden('roles'),
            iconName: 'phosphorBriefcase',
            selected: false
          }
        ]
      },
      {
        label: 'Settings',
        items: [
          {
            label: 'SOP / Rates',
            value: 'rates',
            iconName: 'phosphorPercent',
            hidden: this.isPathHidden('rates'),
            selected: false
          },
          {
            label: 'Billing',
            value: 'billing',
            iconName: 'phosphorCreditCard',
            hidden: this.isPathHidden('billing'),
            selected: false
          },
          {
            label: 'Attendance',
            value: 'attendance',
            iconName: 'phosphorClipboardText',
            hidden: this.isPathHidden('attendance'),
            selected: false
          },
          {
            label: 'Facility Profile',
            value: 'profile',

            hidden: this.isPathHidden('profile'),
            iconName: 'phosphorBuildings',
            selected: false
          }
        ]
      },
      {
        label: '',
        items: [
          {
            label: 'User',
            value: 'user',
            iconName: 'phosphorQuestion',
            selected: false
          },
          {
            label: 'Sign Out',
            value: 'signOut',
            iconName: 'phosphorSignOut',
            selected: false
          }
        ]
      }
    ];
  }

  /**
   * Get current user information for side-nav display
   * Integrates with AuthService to get real user data
   * Returns Observable to support async user data loading
   */
  getCurrentUser(): SideNavUser | undefined {
    const authState = this.authStorage.state;

    if (!authState.lastSessionAt || !authState.user) {
      return undefined;
    }

    const user = authState.user;

    return {
      name: `${user.firstName} ${user.lastName}`,
      role: this.formatRole(authState.role),
      avatarSrc: undefined, // Can be extended to fetch from user profile API
      initials: `${user.firstName[0]}${user.lastName[0]}`.toUpperCase()
    };
  }

  /**
   * Get current user as Observable
   * Useful for reactive updates when auth state changes
   */
  getCurrentUser$(): Observable<SideNavUser | undefined> {
    return this.authStorage.state$.pipe(
      map((authState) => {
        if (!authState.lastSessionAt || !authState.user) {
          return undefined;
        }

        const user = authState.user;

        return {
          name: `${user.firstName} ${user.lastName}`,
          role: this.formatRole(authState.role),
          avatarSrc: undefined,
          initials: `${user.firstName[0]}${user.lastName[0]}`.toUpperCase()
        };
      })
    );
  }

  /**
   * Get route path for a navigation item value
   * Maps nav item values to actual routes
   */
  getRouteForValue(value: string): string {
    return this.pathMap[value]?.path || '/dashboard';
  }

  /**
   * Update selected nav item based on current route
   * Useful for highlighting active navigation item
   */
  updateSelectedItem(
    currentRoute: string,
    groups: SideNavGroup[]
  ): SideNavGroup[] {
    return groups.map((group) => ({
      ...group,
      items: group.items.map((item) => ({
        ...item,
        selected: this.getRouteForValue(item.value) === currentRoute
      }))
    }));
  }

  /**
   * Update badge count for a specific nav item
   * Useful for notifications, pending items, etc.
   */
  updateBadgeCount(
    groups: SideNavGroup[],
    itemValue: string,
    count: number
  ): SideNavGroup[] {
    return groups.map((group) => ({
      ...group,
      items: group.items.map((item) => {
        if (item.value === itemValue) {
          return {
            ...item,
            badge: count,
            showBadge: count > 0
          };
        }
        return item;
      })
    }));
  }

  /**
   * Get navigation filtered by user role/permissions
   * Can be extended to hide nav items based on user permissions
   */
  getNavigationByRole(): SideNavGroup[] {
    const allNavigation = this.getFacilityNavigation();

    // For now, return all navigation
    // Can be extended to filter based on role:
    // if (authState.role === 'FacilityAdmin') {
    //   return allNavigation; // Show all
    // } else if (authState.role === 'ShiftAdministrator') {
    //   return filterNavigation(allNavigation, ['dashboard', 'scheduling']);
    // }

    return allNavigation;
  }

  /**
   * Format user role for display
   * Converts role enum to human-readable format
   */
  private formatRole(role: string | null): string {
    if (!role) return 'User';

    // Convert camelCase/PascalCase to Title Case with spaces
    return role
      .replace(/([A-Z])/g, ' $1')
      .trim()
      .replace(/^./, (str) => str.toUpperCase());
  }

  private isPathHidden(value: string): boolean {
    const permissions = this.pathMap[value]?.permissions;
    if (!permissions) return false;
    return !this.auth.hasPermission(permissions);
  }
}
